// P40 TR-009 -- an iPhone HEIC/HEIF photo becomes a JPEG in the browser
// before it is attached, so the Tutor contract (jpg/jpeg/png/webp, at most
// 10 MB) and Subway are unchanged.
//
// Decoding: the browser's own decoder first (createImageBitmap -- Safari
// decodes HEIC natively); otherwise heic2any, a lazily loaded chunk of its own
// (an emscripten build of libheif/libde265, run in a blob: Web Worker). The
// decoded picture is drawn into a canvas no larger than MAX_EDGE on its long
// side and encoded as JPEG at QUALITY; only if that is over the 10 MB cap is
// the quality stepped down. Orientation: both decoders apply the HEIF
// transformations (irot / imir), so the canvas receives the picture as it is
// meant to be seen.

import { TUTOR_IMAGE_MAX_BYTES } from './imageFile'

export { isHeifHeader } from './imageFile'

export const HEIC_MAX_EDGE = 2560
export const HEIC_JPEG_QUALITY = 0.85
const FALLBACK_QUALITIES = [0.75, 0.65, 0.55]
// A HEIC larger than this is not decoded at all (a phone could not hold it).
export const HEIC_INPUT_MAX_BYTES = 40 * 1024 * 1024

// Doubled in tests.
export const collaborators = {
  loadDecoder: () =>
    import(/* webpackChunkName: "heic-decoder" */ 'heic2any').then(
      module => module.default || module
    ),
  createImageBitmap: (...args) => window.createImageBitmap(...args),
  createCanvas: () => document.createElement('canvas'),
}

async function decode(file) {
  try {
    return await collaborators.createImageBitmap(file)
  } catch (err) {
    // Not decodable natively: the bundled decoder.
  }
  const heic2any = await collaborators.loadDecoder()
  const decoded = await heic2any({ blob: file, toType: 'image/png' })
  const png = Array.isArray(decoded) ? decoded[0] : decoded
  return collaborators.createImageBitmap(png)
}

function toBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('encode'))),
      'image/jpeg',
      quality
    )
  })
}

function jpegName(name) {
  const base = String(name || 'photo').replace(/\.[^.]*$/, '') || 'photo'
  return `${base}.jpg`
}

/**
 * @param {File|Blob} file -- a HEIC/HEIF picture
 * @returns {Promise<File>} the JPEG to attach
 * @throws {{reason: 'heic_failed'|'too_large'}}
 */
export async function convertHeicToJpeg(file) {
  if (!file || file.size > HEIC_INPUT_MAX_BYTES) throw { reason: 'too_large' }
  let bitmap
  try {
    bitmap = await decode(file)
  } catch (err) {
    throw { reason: 'heic_failed' }
  }
  try {
    const scale = Math.min(
      1,
      HEIC_MAX_EDGE / Math.max(bitmap.width, bitmap.height)
    )
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = collaborators.createCanvas()
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    // JPEG has no alpha: a transparent pixel becomes white, not black.
    context.fillStyle = '#fff'
    context.fillRect(0, 0, width, height)
    context.drawImage(bitmap, 0, 0, width, height)
    let blob = null
    for (const quality of [HEIC_JPEG_QUALITY, ...FALLBACK_QUALITIES]) {
      blob = await toBlob(canvas, quality)
      if (blob.size <= TUTOR_IMAGE_MAX_BYTES) break
    }
    if (!blob || blob.size > TUTOR_IMAGE_MAX_BYTES)
      throw { reason: 'too_large' }
    // Free the canvas backing store at once (mobile memory).
    canvas.width = 0
    canvas.height = 0
    return new File([blob], jpegName(file.name), { type: 'image/jpeg' })
  } catch (err) {
    throw err && err.reason ? err : { reason: 'heic_failed' }
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close()
  }
}
